import { useMemo, useState } from "react";
import type { ActivityHistoryItem, ResultItem } from "./types";
import { SubItemContainer, EmphasisText } from "./styles";
import { parseCollections } from "./collections";
import { parseGroups, processAssets } from "./groups";
import { expandBulkActivity } from "./utils";
import { CollectionItem } from "./CollectionItem";
import { GroupItem } from "./GroupItem";
import { useTranslation } from "react-i18next";
import { useMdmEntityDisplayNames } from "../../../../util/mdmEntityDisplayNames";
import { LogTimeline } from "../../../../components/Logs/LogTimeline";

interface ActivityBulkUploadItemProps {
  item: ActivityHistoryItem;
}

export function ActivityBulkUploadItem({ item }: ActivityBulkUploadItemProps) {
  const { t } = useTranslation();
  const { getLabel } = useMdmEntityDisplayNames();
  const [isExpanded, setIsExpanded] = useState(false);

  const { collectionResults, groupResults, assetResults } =
    useMemo(() => {
      const collections: ActivityHistoryItem[] = [];
      const groups: ActivityHistoryItem[] = [];
      const assets: ActivityHistoryItem[] = [];

      expandBulkActivity(item).forEach((it) => {
        const idStr = String(it.id || "");
        if (idStr.includes("-collection-")) {
          collections.push(it);
        } else if (idStr.includes("-group-")) {
          groups.push(it);
        } else if (idStr.includes("-assets") || idStr.includes("-asset")) {
          assets.push(it);
        }
      });

      // Collections are grouped by targetName (e.g. collection name)
      const byTarget = new Map<string, ResultItem[]>();
      parseCollections(collections, t).forEach((result) => {
        const key = result.targetName ?? "";
        const list = byTarget.get(key) ?? [];
        list.push(result);
        byTarget.set(key, list);
      });
      const collectionResults = Array.from(byTarget.values());

      const groupResults = parseGroups(groups, t, undefined, getLabel);

      const assetResults: ResultItem[] = [];
      assets.forEach((assetItem) => {
        const results = processAssets(assetItem, t);
        assetResults.push(...results);
      });

      return { collectionResults, groupResults, assetResults };
    }, [item, t, getLabel]);

  const groupsCount = groupResults.length;
  const collectionsCount = collectionResults.length;
  const assetsCount = assetResults.length;

  const hasChanges = groupsCount + collectionsCount + assetsCount > 0;
  const uploadNumber = item.activity_args?.product_upload_number;

  // An upload that changed no attribute values (e.g. it only attached template
  // groups) still has to show up in the history, so it renders header-only.
  if (!hasChanges && !uploadNumber) {
    return null;
  }

  const parts: string[] = [];
  if (groupsCount > 0) {
    parts.push(
      `${groupsCount} ${groupsCount === 1 ? t("group") : t("groups")}`
    );
  }
  if (collectionsCount > 0) {
    parts.push(
      `${collectionsCount} ${
        collectionsCount === 1 ? t("collection") : t("collections")
      }`
    );
  }
  if (assetsCount > 0) {
    parts.push(
      `${assetsCount} ${assetsCount === 1 ? t("Document") : t("Documents")}`
    );
  }

  const headerLabel = parts.join(" & ");

  const uploadId = uploadNumber ?? item.activity_args?.job_number;

  const message = (
    <>
      {t("Modified")} {headerLabel ? `${headerLabel} ` : ""}
      {t("via")}{" "}
      <EmphasisText>
        {t("Bulk") + " " + (assetsCount > 0 ? t("association") : t("upload"))}
        {uploadId ? ` (${t("Upload ID")}: ${uploadId})` : ""}
      </EmphasisText>
    </>
  );

  const children = hasChanges && (
    <SubItemContainer style={{ paddingLeft: 0 }}>
      {collectionResults.map((results) => (
        <CollectionItem
          key={
            results[0]?.targetName ?? results[0]?.id ?? `col-${results.length}`
          }
          results={results}
          suppressHeader={true}
        />
      ))}
      {groupResults.map((result) => (
        <GroupItem key={result.id} result={result} suppressHeader={true} />
      ))}
      {assetResults.map((result) => (
        <GroupItem key={result.id} result={result} suppressHeader={true} />
      ))}
    </SubItemContainer>
  );

  return (
    <LogTimeline
      id={item.id}
      username={item.last_modified_by}
      date={item.modified_at}
      message={message}
      isExpanded={isExpanded}
      children={children}
      onToggle={() => setIsExpanded(!isExpanded)}
      showToggleButton={hasChanges}
      showBorder={false}
    />
  );
}
